import { Box } from "@mui/material";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { IntegrationServiceType } from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { FundraisingAnalytics } from "~frontend/components/analyticsDashboard/FundraisingAnalytics";
import { OrganizingAnalytics } from "~frontend/components/analyticsDashboard/OrganizingAnalytics";
import {
  StyledTabs,
  StyledTab,
} from "~frontend/components/analyticsDashboard/StyledTabs";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

export default function SubaccountAnalytics(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  useBreadcrumbs([
    {
      label: "Subaccount Analytics",
      path: subaccountLink("/analytics"),
    },
  ]);

  const {
    loading: listIntegrationsLoading,
    error: listIntegrationsError,
    hasIntegration: hasActBlueIntegration,
  } = useIntegrations({ service: IntegrationServiceType.ActBlueWebhook });

  const [activeTab, setActiveTab] = useQueryParam(
    "tab",
    withDefault(
      StringParam,
      hasActBlueIntegration ? "fundraising" : "organizing"
    )
  );

  return (
    <PageContainer>
      <PageHeader>Analytics</PageHeader>
      <AsyncWrapper
        loading={listIntegrationsLoading}
        errors={listIntegrationsError}
      >
        {hasActBlueIntegration ? (
          <StyledTabs
            value={activeTab}
            onChange={(_evt, val) => setActiveTab(val)}
            variant="fullWidth"
          >
            <StyledTab
              label={
                <FormattedMessage
                  defaultMessage="Fundraising"
                  description="Tab header for the fundraising analytics page"
                />
              }
              value={"fundraising"}
            />
            <StyledTab
              label={
                <FormattedMessage
                  defaultMessage="Organizing"
                  description="Tab header for the organizing analytics page"
                />
              }
              value={"organizing"}
            />
          </StyledTabs>
        ) : null}
        <Box sx={{ padding: "40px", backgroundColor: palette.lightGray }}>
          <FundraisingAnalytics
            subaccount={subaccount}
            visible={activeTab === "fundraising"}
          />

          <OrganizingAnalytics
            subaccount={subaccount}
            visible={activeTab === "organizing"}
          />
        </Box>
      </AsyncWrapper>
    </PageContainer>
  );
}
